// src/pages/Library.jsx — Domaine Contenu (Admin → Worker Admin → Worker Contenu)
import React, { useCallback, useEffect, useState } from 'react';
import {
  BookOpen,
  RefreshCw,
  Plus,
  Archive,
  FolderTree,
  HardDrive,
  Layers,
  Activity,
  BarChart3,
  ShieldAlert,
  CheckCircle2,
} from 'lucide-react';
import { PageHeader, EmptyState, HorizontalBars, DonutChart } from '../components/ui/UIComponents';
import { useI18n } from '../i18n/useI18n';
import * as contenu from '../services/adminContenu';

const TYPE_BAR_COLORS = {
  book: 'bg-indigo-500',
  video: 'bg-violet-500',
  audio: 'bg-amber-500',
  training: 'bg-emerald-500',
};

const TYPE_DONUT_COLORS = {
  book: '#4f46e5',
  video: '#7c3aed',
  audio: '#d97706',
  training: '#059669',
};

const CONTENT_TYPES = [
  { value: 'book', label: 'Livre' },
  { value: 'video', label: 'Vidéo' },
  { value: 'audio', label: 'Audio' },
  { value: 'training', label: 'Formation' },
];

const EDITORIAL = [
  { value: 'draft', label: 'Brouillon' },
  { value: 'published', label: 'Publié' },
  { value: 'archived', label: 'Archivé' },
];

const CAT_STATUS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const ASSET_STATUS = [
  { value: 'active', label: 'Actif' },
  { value: 'unavailable', label: 'Indisponible' },
  { value: 'processing', label: 'En traitement' },
];

function typeLabel(t) {
  return CONTENT_TYPES.find((x) => x.value === t)?.label || t || '—';
}

function editorialLabel(s) {
  return EDITORIAL.find((x) => x.value === s)?.label || s || '—';
}

function humanError(err) {
  const raw =
    err?.body?.error ||
    err?.body?.erreur ||
    err?.body?.code ||
    err?.body?.error_code ||
    err?.message ||
    '';
  const code = String(raw);
  const map = {
    INVALID_TYPE: 'Type de contenu non valide.',
    MISSING_TITLE: 'Le titre est obligatoire.',
    INVALID_EDITORIAL_STATUS: 'Statut éditorial non valide.',
    SLUG_ALREADY_EXISTS_FOR_TYPE: 'Ce slug existe déjà pour ce type de contenu.',
    CATEGORY_NOT_FOUND: 'Catégorie introuvable.',
    CATEGORY_TYPE_MISMATCH: 'La catégorie ne correspond pas au type de contenu.',
    CONTENT_NOT_FOUND: 'Contenu introuvable.',
    NO_FIELDS_TO_UPDATE: 'Aucun champ à mettre à jour.',
    MISSING_NAME: 'Le nom est obligatoire.',
    INVALID_CATEGORY_STATUS: 'Statut de catégorie non valide.',
    CATEGORY_SLUG_ALREADY_EXISTS_FOR_TYPE: 'Ce slug de catégorie existe déjà pour ce type.',
    MISSING_PROVIDER_ID: 'Le fournisseur est obligatoire.',
    MISSING_FORMAT: 'Le format est obligatoire.',
    MISSING_REFERENCE: 'La référence est obligatoire.',
    UNKNOWN_PROVIDER: 'Fournisseur inconnu.',
    INVALID_ASSET_STATUS: 'Statut de fichier non valide.',
    ASSET_ALREADY_REGISTERED_FOR_PROVIDER_REFERENCE:
      'Ce fichier est déjà enregistré pour ce fournisseur et cette référence.',
    ASSET_NOT_FOUND: 'Fichier introuvable.',
    PARENT_CONTENT_NOT_FOUND: 'Contenu parent introuvable.',
    MISSING_CHILD_CONTENT_ID: 'L’identifiant du contenu enfant est obligatoire.',
    CHILD_CONTENT_NOT_FOUND: 'Contenu enfant introuvable.',
    CANNOT_LINK_CONTENT_TO_ITSELF: 'Impossible de lier un contenu à lui-même.',
    CYCLE_DETECTED: 'Cette liaison créerait une boucle dans la structure.',
    INVALID_COMPONENT_TYPE: 'Type de composant non valide.',
    COMPONENT_ALREADY_LINKED: 'Ce composant est déjà lié.',
  };
  for (const [k, v] of Object.entries(map)) {
    if (code.includes(k)) return v;
  }
  if (err?.status === 401) return 'Session expirée. Veuillez vous reconnecter.';
  if (err?.status === 403) return 'Vous n’avez pas l’autorisation d’effectuer cette action.';
  if (err?.status === 502) return 'Le service de contenus est temporairement injoignable.';
  if (err?.status === 503) return 'Le service de contenus n’est pas configuré.';
  if (err?.status === 400) return code && !code.startsWith('Erreur') ? code : 'Requête invalide.';
  if (err?.status === 409 || err?.status === 422) return code || 'Opération refusée.';
  return code || 'Une erreur est survenue.';
}

function Alert({ type, children, onClose }) {
  const cls =
    type === 'ok'
      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
      : 'bg-rose-50 border-rose-200 text-rose-800';
  return (
    <div className={`p-3 rounded-2xl border text-xs font-bold flex justify-between gap-2 ${cls}`}>
      <span className="flex items-center gap-2">
        {type === 'ok' ? <CheckCircle2 size={14} /> : <ShieldAlert size={14} />}
        {children}
      </span>
      {onClose && (
        <button type="button" className="underline text-[11px]" onClick={onClose}>
          Fermer
        </button>
      )}
    </div>
  );
}

export default function Library() {
  const { t } = useI18n();
  const [tab, setTab] = useState('stats');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [busy, setBusy] = useState(false);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [lastContent, setLastContent] = useState(null);
  const [lastCategory, setLastCategory] = useState(null);
  const [lastAsset, setLastAsset] = useState(null);
  const [lastComponent, setLastComponent] = useState(null);

  const [cForm, setCForm] = useState({
    type: 'book',
    title: '',
    slug: '',
    description: '',
    language: 'fr',
    category_id: '',
    editorial_status: 'draft',
    level: '',
    duration_seconds: '',
  });
  const [editId, setEditId] = useState('');
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    language: '',
    category_id: '',
    level: '',
    duration_seconds: '',
    editorial_status: '',
  });
  const [archiveId, setArchiveId] = useState('');

  const [catForm, setCatForm] = useState({
    name: '',
    type: 'book',
    slug: '',
    status: 'active',
    parent_id: '',
  });
  const [catEditId, setCatEditId] = useState('');
  const [catEdit, setCatEdit] = useState({ name: '', status: '', parent_id: '' });
  const [catDeactId, setCatDeactId] = useState('');

  const [assetContentId, setAssetContentId] = useState('');
  const [assetForm, setAssetForm] = useState({
    provider_id: '',
    format: '',
    reference: '',
    mime_type: '',
    size_bytes: '',
    duration_seconds: '',
    quality: '',
    language: '',
    delivery_hint: '',
    download_allowed: 'false',
    status: 'active',
    priority: '',
  });
  const [assetEditId, setAssetEditId] = useState('');
  const [assetEdit, setAssetEdit] = useState({
    status: '',
    priority: '',
    size_bytes: '',
    duration_seconds: '',
    quality: '',
    delivery_hint: '',
    mime_type: '',
    download_allowed: '',
  });

  const [compParentId, setCompParentId] = useState('');
  const [compForm, setCompForm] = useState({
    child_content_id: '',
    component_type: 'resource',
    position: '',
  });
  const [compEditId, setCompEditId] = useState('');
  const [compEdit, setCompEdit] = useState({ position: '', component_type: '' });
  const [compDeactId, setCompDeactId] = useState('');

  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilters, setAuditFilters] = useState({
    content_id: '',
    action: '',
    limit: 50,
    offset: 0,
  });

  const [origins, setOrigins] = useState(null);
  const [originsLoading, setOriginsLoading] = useState(false);

  const flash = (msg) => {
    setSuccess(msg);
    setError(null);
  };
  const fail = (err) => {
    setError(humanError(err));
    setSuccess(null);
  };

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setError(null);
    try {
      setStats(await contenu.getContenuStats());
    } catch (err) {
      setStats(null);
      fail(err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    setError(null);
    try {
      const params = { limit: auditFilters.limit, offset: auditFilters.offset };
      if (auditFilters.content_id) params.content_id = auditFilters.content_id;
      if (auditFilters.action) params.action = auditFilters.action;
      const res = await contenu.getContenuAudit(params);
      setAudit(Array.isArray(res?.items) ? res.items : []);
    } catch (err) {
      setAudit([]);
      fail(err);
    } finally {
      setAuditLoading(false);
    }
  }, [auditFilters]);

  const loadOrigins = useCallback(async () => {
    setOriginsLoading(true);
    setError(null);
    try {
      setOrigins(await contenu.getContenuOrigins());
    } catch (err) {
      setOrigins(null);
      fail(err);
    } finally {
      setOriginsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'stats') loadStats();
    if (tab === 'audit') loadAudit();
    if (tab === 'origins') loadOrigins();
  }, [tab, loadStats, loadAudit, loadOrigins]);

  const runCreateContent = async () => {
    setBusy(true);
    try {
      const payload = { type: cForm.type, title: cForm.title.trim() };
      if (cForm.slug.trim()) payload.slug = cForm.slug.trim();
      if (cForm.description.trim()) payload.description = cForm.description.trim();
      if (cForm.language.trim()) payload.language = cForm.language.trim();
      if (cForm.category_id.trim()) payload.category_id = cForm.category_id.trim();
      if (cForm.editorial_status) payload.editorial_status = cForm.editorial_status;
      if (cForm.level.trim()) payload.level = cForm.level.trim();
      if (cForm.duration_seconds !== '') payload.duration_seconds = Number(cForm.duration_seconds);
      setLastContent(await contenu.createContent(payload));
      flash('Contenu créé.');
      setCForm({ ...cForm, title: '', slug: '', description: '' });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const runUpdateContent = async () => {
    if (!editId.trim()) {
      setError('Indiquez l’identifiant du contenu à modifier.');
      return;
    }
    const payload = {};
    if (editForm.title.trim()) payload.title = editForm.title.trim();
    if (editForm.description.trim()) payload.description = editForm.description.trim();
    if (editForm.language.trim()) payload.language = editForm.language.trim();
    if (editForm.category_id.trim()) payload.category_id = editForm.category_id.trim();
    if (editForm.level.trim()) payload.level = editForm.level.trim();
    if (editForm.duration_seconds !== '') payload.duration_seconds = Number(editForm.duration_seconds);
    if (editForm.editorial_status) payload.editorial_status = editForm.editorial_status;
    if (!Object.keys(payload).length) {
      setError('Renseignez au moins un champ à mettre à jour.');
      return;
    }
    setBusy(true);
    try {
      setLastContent(await contenu.updateContent(editId.trim(), payload));
      flash('Contenu mis à jour.');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const runArchive = async () => {
    if (!archiveId.trim()) {
      setError('Indiquez l’identifiant du contenu à archiver.');
      return;
    }
    if (!window.confirm('Archiver ce contenu ?')) return;
    setBusy(true);
    try {
      setLastContent(await contenu.archiveContent(archiveId.trim()));
      flash('Contenu archivé.');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const runCreateCategory = async () => {
    setBusy(true);
    try {
      const payload = { name: catForm.name.trim(), type: catForm.type };
      if (catForm.slug.trim()) payload.slug = catForm.slug.trim();
      if (catForm.status) payload.status = catForm.status;
      if (catForm.parent_id.trim()) payload.parent_id = catForm.parent_id.trim();
      setLastCategory(await contenu.createCategory(payload));
      flash('Catégorie créée.');
      setCatForm({ ...catForm, name: '', slug: '' });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const runUpdateCategory = async () => {
    if (!catEditId.trim()) {
      setError('Indiquez l’identifiant de la catégorie.');
      return;
    }
    const payload = {};
    if (catEdit.name.trim()) payload.name = catEdit.name.trim();
    if (catEdit.status) payload.status = catEdit.status;
    if (catEdit.parent_id.trim()) payload.parent_id = catEdit.parent_id.trim();
    if (!Object.keys(payload).length) {
      setError('Renseignez au moins un champ à mettre à jour.');
      return;
    }
    setBusy(true);
    try {
      setLastCategory(await contenu.updateCategory(catEditId.trim(), payload));
      flash('Catégorie mise à jour.');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const runDeactCategory = async () => {
    if (!catDeactId.trim()) {
      setError('Indiquez l’identifiant de la catégorie.');
      return;
    }
    if (!window.confirm('Désactiver cette catégorie ? Elle ne sera pas supprimée définitivement.')) return;
    setBusy(true);
    try {
      await contenu.deactivateCategory(catDeactId.trim());
      flash('Catégorie désactivée.');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const runCreateAsset = async () => {
    if (!assetContentId.trim()) {
      setError('Indiquez l’identifiant du contenu parent.');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        provider_id: assetForm.provider_id.trim(),
        format: assetForm.format.trim(),
        reference: assetForm.reference.trim(),
      };
      if (assetForm.mime_type.trim()) payload.mime_type = assetForm.mime_type.trim();
      if (assetForm.size_bytes !== '') payload.size_bytes = Number(assetForm.size_bytes);
      if (assetForm.duration_seconds !== '') payload.duration_seconds = Number(assetForm.duration_seconds);
      if (assetForm.quality.trim()) payload.quality = assetForm.quality.trim();
      if (assetForm.language.trim()) payload.language = assetForm.language.trim();
      if (assetForm.delivery_hint.trim()) payload.delivery_hint = assetForm.delivery_hint.trim();
      if (assetForm.download_allowed !== '') payload.download_allowed = assetForm.download_allowed === 'true';
      if (assetForm.status) payload.status = assetForm.status;
      if (assetForm.priority !== '') payload.priority = Number(assetForm.priority);
      setLastAsset(await contenu.createAsset(assetContentId.trim(), payload));
      flash('Fichier associé au contenu.');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const runUpdateAsset = async () => {
    if (!assetEditId.trim()) {
      setError('Indiquez l’identifiant du fichier.');
      return;
    }
    const payload = {};
    if (assetEdit.status) payload.status = assetEdit.status;
    if (assetEdit.priority !== '') payload.priority = Number(assetEdit.priority);
    if (assetEdit.size_bytes !== '') payload.size_bytes = Number(assetEdit.size_bytes);
    if (assetEdit.duration_seconds !== '') payload.duration_seconds = Number(assetEdit.duration_seconds);
    if (assetEdit.quality.trim()) payload.quality = assetEdit.quality.trim();
    if (assetEdit.delivery_hint.trim()) payload.delivery_hint = assetEdit.delivery_hint.trim();
    if (assetEdit.mime_type.trim()) payload.mime_type = assetEdit.mime_type.trim();
    if (assetEdit.download_allowed !== '') payload.download_allowed = assetEdit.download_allowed === 'true';
    if (!Object.keys(payload).length) {
      setError('Renseignez au moins un champ à mettre à jour.');
      return;
    }
    setBusy(true);
    try {
      setLastAsset(await contenu.updateAsset(assetEditId.trim(), payload));
      flash('Fichier mis à jour.');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const runCreateComponent = async () => {
    if (!compParentId.trim()) {
      setError('Indiquez l’identifiant du contenu parent.');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        child_content_id: Number(compForm.child_content_id) || compForm.child_content_id,
      };
      if (compForm.component_type.trim()) payload.component_type = compForm.component_type.trim();
      if (compForm.position !== '') payload.position = Number(compForm.position);
      setLastComponent(await contenu.createComponent(compParentId.trim(), payload));
      flash('Composant lié.');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const runUpdateComponent = async () => {
    if (!compEditId.trim()) {
      setError('Indiquez l’identifiant du composant.');
      return;
    }
    const payload = {};
    if (compEdit.position !== '') payload.position = Number(compEdit.position);
    if (compEdit.component_type.trim()) payload.component_type = compEdit.component_type.trim();
    if (!Object.keys(payload).length) {
      setError('Renseignez au moins un champ à mettre à jour.');
      return;
    }
    setBusy(true);
    try {
      setLastComponent(await contenu.updateComponent(compEditId.trim(), payload));
      flash('Composant mis à jour.');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const runDeactComponent = async () => {
    if (!compDeactId.trim()) {
      setError('Indiquez l’identifiant du composant.');
      return;
    }
    if (!window.confirm('Désactiver ce composant ? Il ne sera pas supprimé définitivement.')) return;
    setBusy(true);
    try {
      await contenu.deactivateComponent(compDeactId.trim());
      flash('Composant désactivé.');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const tabBtn = (id, label, Icon) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold border-b-2 -mb-px ${
        tab === id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
    >
      {Icon && <Icon size={14} />} {label}
    </button>
  );

  const ResultCard = ({ title, data }) => {
    if (!data) return null;
    const id = data.id ?? data.content_id ?? data.category_id;
    return (
      <div className="dsi-card p-4 space-y-1 text-xs border border-emerald-100 bg-emerald-50/40">
        <p className="font-bold text-slate-900">{title}</p>
        {id != null && <p>Identifiant : {String(id)}</p>}
        {data.title && <p>Titre : {data.title}</p>}
        {data.name && <p>Nom : {data.name}</p>}
        {data.type && <p>Type : {typeLabel(data.type)}</p>}
        {data.editorial_status && <p>Statut : {editorialLabel(data.editorial_status)}</p>}
        {data.status && <p>Statut : {String(data.status)}</p>}
        {data.slug && <p>Slug : {data.slug}</p>}
      </div>
    );
  };

  const byType = Array.isArray(stats?.by_type) ? stats.by_type : [];
  const originList = Array.isArray(origins?.origins)
    ? origins.origins
    : Array.isArray(origins?.items)
      ? origins.items
      : Array.isArray(origins)
        ? origins
        : [];

  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title={t('pages.library.title')}
        subtitle="Catalogue pédagogique : contenus, catégories, fichiers et structure de formation."
      />

      {error && (
        <Alert type="err" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="ok" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <div className="flex gap-1 border-b border-slate-200 flex-wrap">
        {tabBtn('stats', 'Vue d’ensemble', BarChart3)}
        {tabBtn('contents', 'Contenus', BookOpen)}
        {tabBtn('categories', 'Catégories', FolderTree)}
        {tabBtn('assets', 'Fichiers', HardDrive)}
        {tabBtn('components', 'Composants', Layers)}
        {tabBtn('audit', 'Historique', Activity)}
        {tabBtn('origins', 'Origines', ShieldAlert)}
      </div>

      {tab === 'stats' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button type="button" className="dsi-btn-secondary text-xs" onClick={loadStats} disabled={statsLoading}>
              <RefreshCw size={14} className={statsLoading ? 'animate-spin' : ''} /> Actualiser
            </button>
          </div>
          {statsLoading ? (
            <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement des statistiques…</div>
          ) : !byType.length && !(typeof stats?.grand_total === 'number' && stats.grand_total > 0) ? (
            <EmptyState
              title="Catalogue vide"
              message="Aucun contenu n’a encore été comptabilisé. Les graphiques apparaîtront dès que le catalogue contiendra des éléments."
              onRetry={loadStats}
            />
          ) : (
            <>
              {/* Indicateur global */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="dsi-card p-5 sm:col-span-1">
                  <p className="text-[11px] uppercase text-slate-500 font-semibold">Total du catalogue</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">
                    {typeof stats?.grand_total === 'number' ? stats.grand_total : '—'}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-1">Ensemble des contenus recensés</p>
                </div>
                <div className="dsi-card p-5 sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <p className="text-[11px] uppercase text-slate-500 font-semibold mb-2">Répartition par type</p>
                    {byType.length ? (
                      <DonutChart
                        centerLabel="Total"
                        centerValue={
                          typeof stats?.grand_total === 'number'
                            ? stats.grand_total
                            : byType.reduce((s, r) => s + (Number(r.total_count) || 0), 0)
                        }
                        segments={byType.map((row) => ({
                          label: typeLabel(row.type),
                          value: Number(row.total_count) || 0,
                          color: TYPE_DONUT_COLORS[row.type] || '#64748b',
                        }))}
                      />
                    ) : (
                      <p className="text-xs text-slate-400">Aucune répartition disponible.</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Histogramme — contenus par type */}
              <div className="dsi-card p-5">
                <h3 className="text-sm font-bold text-slate-900 mb-1">Volumes par type</h3>
                <p className="text-[11px] text-slate-500 mb-4">Nombre total de contenus pour chaque type présent.</p>
                {byType.length ? (
                  <HorizontalBars
                    rows={byType.map((row) => ({
                      label: typeLabel(row.type),
                      value: Number(row.total_count) || 0,
                      color: TYPE_BAR_COLORS[row.type] || 'bg-slate-400',
                    }))}
                  />
                ) : (
                  <p className="text-xs text-slate-400">Aucun type à afficher.</p>
                )}
              </div>

              {/* Histogramme — total vs publiés */}
              <div className="dsi-card p-5">
                <h3 className="text-sm font-bold text-slate-900 mb-1">Total et publiés par type</h3>
                <p className="text-[11px] text-slate-500 mb-4">
                  Comparaison du volume total et de la part publiée, type par type.
                </p>
                {byType.length ? (
                  <div className="space-y-5">
                    {byType.map((row) => {
                      const total = Number(row.total_count) || 0;
                      const published = Number(row.published_count) || 0;
                      const max = Math.max(total, published, 1);
                      return (
                        <div key={row.type}>
                          <div className="flex items-center justify-between text-xs mb-1.5">
                            <span className="font-semibold text-slate-800">{typeLabel(row.type)}</span>
                            <span className="text-slate-500">
                              {published} publiés / {total} au total
                            </span>
                          </div>
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-2">
                              <span className="w-16 text-[10px] text-slate-500 shrink-0">Total</span>
                              <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${TYPE_BAR_COLORS[row.type] || 'bg-slate-400'}`}
                                  style={{ width: `${Math.round((total / max) * 100)}%` }}
                                  title={`Total : ${total}`}
                                />
                              </div>
                              <span className="w-8 text-right text-[11px] font-semibold text-slate-800">{total}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="w-16 text-[10px] text-slate-500 shrink-0">Publiés</span>
                              <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-emerald-500/90"
                                  style={{ width: `${Math.round((published / max) * 100)}%` }}
                                  title={`Publiés : ${published}`}
                                />
                              </div>
                              <span className="w-8 text-right text-[11px] font-semibold text-slate-800">{published}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div className="flex flex-wrap gap-4 pt-2 border-t border-slate-100 text-[10px] text-slate-500">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-indigo-500" /> Volume total
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Publiés
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">Aucune comparaison disponible.</p>
                )}
              </div>

              {/* Cartes détail par type (données réelles) */}
              {byType.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {byType.map((row) => (
                    <div key={row.type} className="dsi-card p-4 space-y-1">
                      <p className="text-xs font-bold text-slate-900">{typeLabel(row.type)}</p>
                      <p className="text-lg font-bold">{row.total_count ?? '—'}</p>
                      <p className="text-[11px] text-slate-500">Publiés : {row.published_count ?? '—'}</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'contents' && (
        <div className="space-y-6">
          <p className="text-xs text-slate-500">
            Aucune liste globale n’est fournie par l’administration pour le moment. Vous pouvez créer un contenu, le
            modifier ou l’archiver à partir de son identifiant.
          </p>
          <div className="dsi-card p-5 space-y-3">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Plus size={16} /> Créer un contenu
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <label className="space-y-1">
                <span className="font-semibold text-slate-600">Type</span>
                <select className="dsi-input" value={cForm.type} onChange={(e) => setCForm({ ...cForm, type: e.target.value })}>
                  {CONTENT_TYPES.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label className="space-y-1">
                <span className="font-semibold text-slate-600">Titre</span>
                <input className="dsi-input" value={cForm.title} onChange={(e) => setCForm({ ...cForm, title: e.target.value })} />
              </label>
              <label className="space-y-1">
                <span className="font-semibold text-slate-600">Slug (optionnel)</span>
                <input className="dsi-input" value={cForm.slug} onChange={(e) => setCForm({ ...cForm, slug: e.target.value })} />
              </label>
              <label className="space-y-1">
                <span className="font-semibold text-slate-600">Langue</span>
                <input className="dsi-input" value={cForm.language} onChange={(e) => setCForm({ ...cForm, language: e.target.value })} />
              </label>
              <label className="space-y-1 sm:col-span-2">
                <span className="font-semibold text-slate-600">Description</span>
                <textarea className="dsi-input" rows={2} value={cForm.description} onChange={(e) => setCForm({ ...cForm, description: e.target.value })} />
              </label>
              <label className="space-y-1">
                <span className="font-semibold text-slate-600">Catégorie (identifiant)</span>
                <input className="dsi-input" value={cForm.category_id} onChange={(e) => setCForm({ ...cForm, category_id: e.target.value })} />
              </label>
              <label className="space-y-1">
                <span className="font-semibold text-slate-600">Statut éditorial</span>
                <select className="dsi-input" value={cForm.editorial_status} onChange={(e) => setCForm({ ...cForm, editorial_status: e.target.value })}>
                  {EDITORIAL.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label className="space-y-1">
                <span className="font-semibold text-slate-600">Niveau</span>
                <input className="dsi-input" value={cForm.level} onChange={(e) => setCForm({ ...cForm, level: e.target.value })} />
              </label>
              <label className="space-y-1">
                <span className="font-semibold text-slate-600">Durée (secondes)</span>
                <input className="dsi-input" type="number" min={0} value={cForm.duration_seconds} onChange={(e) => setCForm({ ...cForm, duration_seconds: e.target.value })} />
              </label>
            </div>
            <button type="button" className="dsi-btn-primary text-xs" disabled={busy || !cForm.title.trim()} onClick={runCreateContent}>
              Créer
            </button>
          </div>
          <div className="dsi-card p-5 space-y-3">
            <h3 className="text-sm font-bold">Modifier un contenu</h3>
            <input className="dsi-input text-xs" placeholder="Identifiant" value={editId} onChange={(e) => setEditId(e.target.value)} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <input className="dsi-input" placeholder="Titre" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
              <input className="dsi-input" placeholder="Langue" value={editForm.language} onChange={(e) => setEditForm({ ...editForm, language: e.target.value })} />
              <input className="dsi-input" placeholder="Catégorie (identifiant)" value={editForm.category_id} onChange={(e) => setEditForm({ ...editForm, category_id: e.target.value })} />
              <select className="dsi-input" value={editForm.editorial_status} onChange={(e) => setEditForm({ ...editForm, editorial_status: e.target.value })}>
                <option value="">Statut (inchangé)</option>
                {EDITORIAL.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <input className="dsi-input" placeholder="Niveau" value={editForm.level} onChange={(e) => setEditForm({ ...editForm, level: e.target.value })} />
              <input className="dsi-input" type="number" placeholder="Durée (s)" value={editForm.duration_seconds} onChange={(e) => setEditForm({ ...editForm, duration_seconds: e.target.value })} />
              <textarea className="dsi-input sm:col-span-2" rows={2} placeholder="Description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
            </div>
            <button type="button" className="dsi-btn-secondary text-xs" disabled={busy} onClick={runUpdateContent}>
              Enregistrer
            </button>
          </div>
          <div className="dsi-card p-5 space-y-3">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Archive size={16} /> Archiver un contenu
            </h3>
            <div className="flex flex-wrap gap-2">
              <input className="dsi-input flex-1 min-w-[160px]" placeholder="Identifiant" value={archiveId} onChange={(e) => setArchiveId(e.target.value)} />
              <button type="button" className="dsi-btn-secondary text-xs" disabled={busy} onClick={runArchive}>
                Archiver
              </button>
            </div>
          </div>
          <ResultCard title="Dernier contenu traité" data={lastContent} />
        </div>
      )}

      {tab === 'categories' && (
        <div className="space-y-6">
          <div className="dsi-card p-5 space-y-3">
            <h3 className="text-sm font-bold">Créer une catégorie</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <input className="dsi-input" placeholder="Nom" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} />
              <select className="dsi-input" value={catForm.type} onChange={(e) => setCatForm({ ...catForm, type: e.target.value })}>
                {CONTENT_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <input className="dsi-input" placeholder="Slug (optionnel)" value={catForm.slug} onChange={(e) => setCatForm({ ...catForm, slug: e.target.value })} />
              <select className="dsi-input" value={catForm.status} onChange={(e) => setCatForm({ ...catForm, status: e.target.value })}>
                {CAT_STATUS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <input className="dsi-input" placeholder="Parent (identifiant, optionnel)" value={catForm.parent_id} onChange={(e) => setCatForm({ ...catForm, parent_id: e.target.value })} />
            </div>
            <button type="button" className="dsi-btn-primary text-xs" disabled={busy || !catForm.name.trim()} onClick={runCreateCategory}>
              Créer
            </button>
          </div>
          <div className="dsi-card p-5 space-y-3">
            <h3 className="text-sm font-bold">Modifier une catégorie</h3>
            <input className="dsi-input text-xs" placeholder="Identifiant" value={catEditId} onChange={(e) => setCatEditId(e.target.value)} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <input className="dsi-input" placeholder="Nom" value={catEdit.name} onChange={(e) => setCatEdit({ ...catEdit, name: e.target.value })} />
              <select className="dsi-input" value={catEdit.status} onChange={(e) => setCatEdit({ ...catEdit, status: e.target.value })}>
                <option value="">Statut (inchangé)</option>
                {CAT_STATUS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <input className="dsi-input" placeholder="Parent (identifiant)" value={catEdit.parent_id} onChange={(e) => setCatEdit({ ...catEdit, parent_id: e.target.value })} />
            </div>
            <button type="button" className="dsi-btn-secondary text-xs" disabled={busy} onClick={runUpdateCategory}>
              Enregistrer
            </button>
          </div>
          <div className="dsi-card p-5 space-y-3">
            <h3 className="text-sm font-bold">Désactiver une catégorie</h3>
            <p className="text-[11px] text-slate-500">La catégorie reste conservée ; elle n’est plus active.</p>
            <div className="flex flex-wrap gap-2">
              <input className="dsi-input flex-1" placeholder="Identifiant" value={catDeactId} onChange={(e) => setCatDeactId(e.target.value)} />
              <button type="button" className="dsi-btn-secondary text-xs" disabled={busy} onClick={runDeactCategory}>
                Désactiver
              </button>
            </div>
          </div>
          <ResultCard title="Dernière catégorie traitée" data={lastCategory} />
        </div>
      )}

      {tab === 'assets' && (
        <div className="space-y-6">
          <div className="dsi-card p-5 space-y-3">
            <h3 className="text-sm font-bold">Associer un fichier à un contenu</h3>
            <input className="dsi-input text-xs" placeholder="Identifiant du contenu" value={assetContentId} onChange={(e) => setAssetContentId(e.target.value)} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <input className="dsi-input" placeholder="Fournisseur" value={assetForm.provider_id} onChange={(e) => setAssetForm({ ...assetForm, provider_id: e.target.value })} />
              <input className="dsi-input" placeholder="Format" value={assetForm.format} onChange={(e) => setAssetForm({ ...assetForm, format: e.target.value })} />
              <input className="dsi-input sm:col-span-2" placeholder="Référence" value={assetForm.reference} onChange={(e) => setAssetForm({ ...assetForm, reference: e.target.value })} />
              <input className="dsi-input" placeholder="Type MIME" value={assetForm.mime_type} onChange={(e) => setAssetForm({ ...assetForm, mime_type: e.target.value })} />
              <input className="dsi-input" type="number" placeholder="Taille (octets)" value={assetForm.size_bytes} onChange={(e) => setAssetForm({ ...assetForm, size_bytes: e.target.value })} />
              <select className="dsi-input" value={assetForm.status} onChange={(e) => setAssetForm({ ...assetForm, status: e.target.value })}>
                {ASSET_STATUS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <select className="dsi-input" value={assetForm.download_allowed} onChange={(e) => setAssetForm({ ...assetForm, download_allowed: e.target.value })}>
                <option value="false">Téléchargement interdit</option>
                <option value="true">Téléchargement autorisé</option>
              </select>
              <input className="dsi-input" type="number" placeholder="Priorité" value={assetForm.priority} onChange={(e) => setAssetForm({ ...assetForm, priority: e.target.value })} />
              <input className="dsi-input" placeholder="Indication de livraison" value={assetForm.delivery_hint} onChange={(e) => setAssetForm({ ...assetForm, delivery_hint: e.target.value })} />
            </div>
            <button type="button" className="dsi-btn-primary text-xs" disabled={busy} onClick={runCreateAsset}>
              Associer
            </button>
          </div>
          <div className="dsi-card p-5 space-y-3">
            <h3 className="text-sm font-bold">Modifier un fichier</h3>
            <input className="dsi-input text-xs" placeholder="Identifiant du fichier" value={assetEditId} onChange={(e) => setAssetEditId(e.target.value)} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <select className="dsi-input" value={assetEdit.status} onChange={(e) => setAssetEdit({ ...assetEdit, status: e.target.value })}>
                <option value="">Statut (inchangé)</option>
                {ASSET_STATUS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <select className="dsi-input" value={assetEdit.download_allowed} onChange={(e) => setAssetEdit({ ...assetEdit, download_allowed: e.target.value })}>
                <option value="">Téléchargement (inchangé)</option>
                <option value="false">Interdit</option>
                <option value="true">Autorisé</option>
              </select>
              <input className="dsi-input" type="number" placeholder="Priorité" value={assetEdit.priority} onChange={(e) => setAssetEdit({ ...assetEdit, priority: e.target.value })} />
              <input className="dsi-input" type="number" placeholder="Taille (octets)" value={assetEdit.size_bytes} onChange={(e) => setAssetEdit({ ...assetEdit, size_bytes: e.target.value })} />
            </div>
            <button type="button" className="dsi-btn-secondary text-xs" disabled={busy} onClick={runUpdateAsset}>
              Enregistrer
            </button>
          </div>
          <ResultCard title="Dernier fichier traité" data={lastAsset} />
        </div>
      )}

      {tab === 'components' && (
        <div className="space-y-6">
          <div className="dsi-card p-5 space-y-3">
            <h3 className="text-sm font-bold">Lier un composant à une formation</h3>
            <input className="dsi-input text-xs" placeholder="Identifiant du contenu parent" value={compParentId} onChange={(e) => setCompParentId(e.target.value)} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <input className="dsi-input" placeholder="Contenu enfant (identifiant)" value={compForm.child_content_id} onChange={(e) => setCompForm({ ...compForm, child_content_id: e.target.value })} />
              <input className="dsi-input" placeholder="Type (défaut : resource)" value={compForm.component_type} onChange={(e) => setCompForm({ ...compForm, component_type: e.target.value })} />
              <input className="dsi-input" type="number" placeholder="Position" value={compForm.position} onChange={(e) => setCompForm({ ...compForm, position: e.target.value })} />
            </div>
            <button type="button" className="dsi-btn-primary text-xs" disabled={busy} onClick={runCreateComponent}>
              Lier
            </button>
          </div>
          <div className="dsi-card p-5 space-y-3">
            <h3 className="text-sm font-bold">Modifier un composant</h3>
            <input className="dsi-input text-xs" placeholder="Identifiant du composant" value={compEditId} onChange={(e) => setCompEditId(e.target.value)} />
            <div className="grid grid-cols-2 gap-2 text-xs">
              <input className="dsi-input" type="number" placeholder="Position" value={compEdit.position} onChange={(e) => setCompEdit({ ...compEdit, position: e.target.value })} />
              <input className="dsi-input" placeholder="Type" value={compEdit.component_type} onChange={(e) => setCompEdit({ ...compEdit, component_type: e.target.value })} />
            </div>
            <button type="button" className="dsi-btn-secondary text-xs" disabled={busy} onClick={runUpdateComponent}>
              Enregistrer
            </button>
          </div>
          <div className="dsi-card p-5 space-y-3">
            <h3 className="text-sm font-bold">Désactiver un composant</h3>
            <p className="text-[11px] text-slate-500">Désactivation sans suppression définitive.</p>
            <div className="flex gap-2 flex-wrap">
              <input className="dsi-input flex-1" placeholder="Identifiant" value={compDeactId} onChange={(e) => setCompDeactId(e.target.value)} />
              <button type="button" className="dsi-btn-secondary text-xs" disabled={busy} onClick={runDeactComponent}>
                Désactiver
              </button>
            </div>
          </div>
          <ResultCard title="Dernier composant traité" data={lastComponent} />
        </div>
      )}

      {tab === 'audit' && (
        <div className="space-y-4">
          <div className="dsi-card p-4 flex flex-wrap gap-3 items-end">
            <label className="text-xs space-y-1">
              <span className="font-semibold text-slate-600">Contenu (identifiant)</span>
              <input className="dsi-input w-36" value={auditFilters.content_id} onChange={(e) => setAuditFilters({ ...auditFilters, content_id: e.target.value })} />
            </label>
            <label className="text-xs space-y-1">
              <span className="font-semibold text-slate-600">Action</span>
              <input className="dsi-input w-36" value={auditFilters.action} onChange={(e) => setAuditFilters({ ...auditFilters, action: e.target.value })} />
            </label>
            <label className="text-xs space-y-1">
              <span className="font-semibold text-slate-600">Limite</span>
              <input type="number" min={1} className="dsi-input w-24" value={auditFilters.limit} onChange={(e) => setAuditFilters({ ...auditFilters, limit: Number(e.target.value) || 50 })} />
            </label>
            <label className="text-xs space-y-1">
              <span className="font-semibold text-slate-600">À partir de</span>
              <input type="number" min={0} className="dsi-input w-24" value={auditFilters.offset} onChange={(e) => setAuditFilters({ ...auditFilters, offset: Number(e.target.value) || 0 })} />
            </label>
            <button type="button" className="dsi-btn-secondary text-xs" onClick={loadAudit} disabled={auditLoading}>
              <RefreshCw size={14} className={auditLoading ? 'animate-spin' : ''} /> Actualiser
            </button>
          </div>
          {auditLoading ? (
            <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement…</div>
          ) : (
            <div className="dsi-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 font-semibold border-b">
                    <tr>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Action</th>
                      <th className="px-3 py-2">Contenu</th>
                      <th className="px-3 py-2">Auteur</th>
                      <th className="px-3 py-2">Détail</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {audit.map((row) => (
                      <tr key={row.id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-[11px] whitespace-nowrap">{row.created_at || '—'}</td>
                        <td className="px-3 py-2 text-xs font-semibold">{row.action || '—'}</td>
                        <td className="px-3 py-2 text-[11px] font-mono">{row.content_id ?? '—'}</td>
                        <td className="px-3 py-2 text-[11px]">{row.actor || '—'}</td>
                        <td className="px-3 py-2 text-[11px] text-slate-600 max-w-[280px] truncate">
                          {typeof row.detail === 'string' ? row.detail : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!audit.length && <EmptyState title="Aucun événement" message="Aucun historique pour ces filtres." />}
            </div>
          )}
        </div>
      )}

      {tab === 'origins' && (
        <div className="space-y-4">
          <div className="dsi-card p-5 space-y-3">
            <h3 className="text-sm font-bold">Origines du service Contenus</h3>
            <p className="text-xs text-slate-600">
              Consultation uniquement. L’ajout, la modification ou la désactivation se font depuis le centre de
              contrôle, section « Origines autorisées ».
            </p>
            <button type="button" className="dsi-btn-secondary text-xs" onClick={loadOrigins} disabled={originsLoading}>
              <RefreshCw size={14} className={originsLoading ? 'animate-spin' : ''} /> Actualiser
            </button>
          </div>
          {originsLoading ? (
            <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement…</div>
          ) : (
            <div className="dsi-card p-4">
              <ul className="space-y-2 text-xs">
                {originList.map((o, i) => (
                  <li key={o.id || o.origin || i} className="flex flex-wrap gap-2 border-b border-slate-100 pb-2">
                    <span className="font-semibold">{o.origin || o.url || '—'}</span>
                    {o.label && <span className="text-slate-500">{o.label}</span>}
                    {o.status && <span className="text-slate-500">· {o.status}</span>}
                  </li>
                ))}
              </ul>
              {!originList.length && (
                <EmptyState title="Aucune origine" message="Aucune origine listée pour ce service." />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
